// Pure helpers for the Policy Simulator section. Keep this free of React
// and fetch so it can be unit-tested directly (the repo's test convention —
// see tenantSearch.ts / liveChatFilters.ts). The PolicySimulator component
// in App.tsx renders the output of classifyFields().

import type { SimulateResult } from "./api";

export type FieldVisibility = "visible" | "masked" | "hidden";

export interface ClassifiedField {
  field: string;
  visibility: FieldVisibility;
  /** Masking strategy when visibility === "masked"; null otherwise. */
  strategy: string | null;
}

/**
 * Given the full list of an object's fields and a /policy/simulate result,
 * classify each field as:
 *   - "visible": present in result.visible_fields
 *   - "masked":  present in result.masked_fields (carries the strategy)
 *   - "hidden":  in neither list (not readable for this role)
 *
 * Masked takes precedence over visible if a field somehow appears in both
 * (a masked field is still "seen but obscured", which is the stronger signal
 * to surface to the operator). Fields are returned in the input order so the
 * table is stable.
 */
export function classifyFields(
  allFields: ReadonlyArray<string>,
  result: SimulateResult,
): ClassifiedField[] {
  const visible = new Set(result.visible_fields ?? []);
  const maskedByField = new Map(
    (result.masked_fields ?? []).map((m) => [m.field, m.strategy]),
  );

  return allFields.map((field) => {
    if (maskedByField.has(field)) {
      return {
        field,
        visibility: "masked",
        strategy: maskedByField.get(field) ?? "redact",
      };
    }
    if (visible.has(field)) {
      return { field, visibility: "visible", strategy: null };
    }
    return { field, visibility: "hidden", strategy: null };
  });
}

/**
 * Union of every field referenced by a simulate result — used when the
 * caller doesn't already know the object's full field list (e.g. the result
 * came back before a definition was loaded). Preserves first-seen order:
 * visible fields first, then any masked-only fields.
 */
export function fieldsFromResult(result: SimulateResult): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of result.visible_fields ?? []) {
    if (!seen.has(f)) {
      seen.add(f);
      out.push(f);
    }
  }
  for (const m of result.masked_fields ?? []) {
    if (!seen.has(m.field)) {
      seen.add(m.field);
      out.push(m.field);
    }
  }
  return out;
}

/** Tailwind tone token per visibility, mirroring the Badge tones in App.tsx. */
export function visibilityTone(v: FieldVisibility): "good" | "warn" | "bad" {
  switch (v) {
    case "visible":
      return "good";
    case "masked":
      return "warn";
    case "hidden":
      return "bad";
  }
}
